import { useEffect, useState } from 'react';
import { LocationEventDetailRepository } from '../../data/repositories/LocationEventDetailRepository';
import type { LocationEvent } from '../../domain/models/locations/LocationEvent';
import { GetLocationEventDetailUseCase } from '../../domain/usecases/GetLocationEventDetailUseCase';
import { ToggleLocationLikeUseCase } from '../../domain/usecases/ToggleLocationLikeUseCase';

interface UseLocationEventDetailResult {
  eventDetail: LocationEvent | null;
  isLoading: boolean;
  error: Error | null;
  toggleLike: () => Promise<boolean>;
}

export function useLocationEventDetail(
  locationId: string,
  eventId: string
): UseLocationEventDetailResult {
  const [eventDetail, setEventDetail] = useState<LocationEvent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Initialize repository and use cases
  const repository = new LocationEventDetailRepository();
  const getLocationEventDetailUseCase = new GetLocationEventDetailUseCase(repository);
  const toggleLocationLikeUseCase = new ToggleLocationLikeUseCase(repository);

  useEffect(() => {
    let isMounted = true;

    const fetchEventDetail = async () => {
      if (!locationId || !eventId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getLocationEventDetailUseCase.execute(locationId, eventId);
        if (isMounted) {
          setEventDetail(data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchEventDetail();

    return () => {
      isMounted = false;
    };
  }, [locationId, eventId, getLocationEventDetailUseCase.execute]);

  const toggleLike = async (): Promise<boolean> => {
    if (!locationId) return false;

    try {
      const isLiked = await toggleLocationLikeUseCase.execute(locationId);

      // Update local state with the new like status
      if (eventDetail) {
        // Need to create a new event detail with updated like status
        // This is a temporary solution until we implement a more proper state management
        setEventDetail((prev) => {
          if (!prev) return null;

          // Create a new location with updated isLiked status
          const updatedLocation = Object.assign(
            Object.create(Object.getPrototypeOf(prev.location)),
            {
              ...prev.location,
              _isLiked: isLiked
            }
          );

          // Create a new event detail with the updated location
          return Object.assign(Object.create(Object.getPrototypeOf(prev)), {
            ...prev,
            _location: updatedLocation
          });
        });
      }

      return isLiked;
    } catch (err) {
      setError(err as Error);
      return false;
    }
  };

  return { eventDetail, isLoading, error, toggleLike };
}
